<template>
  <div
    v-if="plan"
    :class="{
      'flex h-full flex-col border border-b-4 border-solid border-www-color-neutral-very-light border-b-www-color-primary-lighter bg-www-color-white shadow-[0_0_20px_0_rgba(0,0,0,0.15)]': true,
      'text-center transition-all duration-300 ease-in-out': true,
      'group cursor-pointer hover:border-www-color-primary-lighter': true,
      'border-www-color-primary-lighter': highlighPlan,
    }"
  >
    <div class="flex-grow p-4">
      <h3
        :class="{
          'mb-4 text-xl font-extrabold text-www-color-neutral-medium-dark group-hover:text-www-color-primary-lighter': true,
          'text-www-color-primary-lighter': highlighPlan,
        }"
      >
        {{ plan.name }}
      </h3>
      <p
        :class="{
          'text-10px font-bold leading-5 text-www-color-neutral-medium-light': true,
        }"
      >
        {{ plan.tagline }}
      </p>
      <div
        :class="{
          '-mx-4 mb-9 mt-5 h-1px bg-www-color-neutral-very-light': true,
        }"
      ></div>
      <div
        :class="{
          'mb-4 h-70px': true,
        }"
      >
        <span
          :class="[
            'font-extrabold text-www-color-neutral-medium-dark group-hover:text-www-color-primary-lighter',
            { 'text-www-color-primary-lighter': highlighPlan },
          ]"
        >
          <PersonalizedSectionBuilder
            v-if="cost"
            :data="cost"
            component-type=""
            :translate="cost.excludeFromTranslation === true ? 'no' : undefined"
            :class="{
              'mx-auto justify-center': true,
            }"
          />
        </span>
        <span class="block min-h-3 text-center text-10px font-bold text-www-color-neutral-light">{{
          plan.priceDescription
        }}</span>
      </div>
      <Button
        v-if="plan.CTAButton"
        v-bind="{ buttonProps: plan.CTAButton }"
        :class="{
          'inline-block': true,
          'group-hover:border-www-color-primary-lighter group-hover:bg-www-color-primary-light group-hover:text-www-color-white': true,
          'border-www-color-primary-lighter bg-www-color-primary-light text-www-color-white':
            highlighPlan,
        }"
      />
      <div class="mx-[-1rem] mb-9 mt-5 h-1px bg-www-color-neutral-very-light"></div>
      <Transition name="slide">
        <div
          v-if="showList"
          ref="contentRef"
        >
          <PortableText
            v-if="plan.summaryList?.standardPortableText"
            :portable-data="plan.summaryList?.standardPortableText"
            :class="{
              'g-0': true,
            }"
          />
        </div>
      </Transition>
      <h2
        :class="{
          'accordion-header block lg:hidden': true,
        }"
      >
        <button
          :class="{
            'pt-features-mobile collapsed': true,
          }"
          type="button"
          data-bs-toggle="collapse"
          data-bs-target="#Free"
          aria-expanded="false"
          aria-controls="Free"
          @click="toggleFeatures"
        >
          <span
            v-if="!showList"
            :class="{
              'show-features cursor-pointer font-sans text-sm font-medium leading-9 text-www-color-accent-teal-soft': true,
            }"
            >{{ plan?.showFeatures
            }}<span
              :class="{
                '-mb-1 ml-6px inline-flex h-5 w-5 items-center justify-center rounded-full border': true,
              }"
              >+</span
            ></span
          >
          <span
            v-else
            :class="{
              'hide-features cursor-pointer font-sans text-sm font-medium leading-9 text-www-color-accent-teal-soft': true,
            }"
            >{{ plan.hideFeatures
            }}<span
              :class="{
                '-mb-1 ml-6px inline-flex h-5 w-5 items-center justify-center rounded-full border': true,
              }"
              >-</span
            ></span
          >
        </button>
      </h2>
    </div>
    <PortableText
      v-if="plan.highlight?.standardPortableText"
      :portable-data="plan.highlight?.standardPortableText"
      :class="{
        'g-0 mb-0 bg-www-color-accent-blue-soft p-4 text-center text-xs font-extrabold italic leading-5 text-www-color-neutral-medium-dark': true,
      }"
    />
    <div
      :class="{
        'description relative h-24 bg-www-color-neutral-off-white px-4 py-4 text-xs font-medium leading-5 text-www-color-neutral-medium-dark': true,
        'border-t border-www-color-neutral-very-light': true,
      }"
    >
      {{ plan.description }}
    </div>
  </div>
</template>
<script setup lang="ts">
import PersonalizedSectionBuilder from '~/components/Atmos/PersonalizedSectionBuilder/PersonalizedSectionBuilder.vue';
import PortableText from '~/components/Atmos/PortableText/PortableText.vue';
import { useResponsive } from '~/composables/useResponsive';
import type {
  HighlightedPlan,
  PersonalizedSectionBuilderRefQueryResult,
  PlanRefQueryResult,
} from '~/sanity/types';
import { useRefsStore } from '~/composables/refsStore';
import Button from '~/components/Atmos/Button/Button.vue';

const showList = ref(true);
const props = defineProps<HighlightedPlan>();

const { getResolvedPlan, getResolvedPersonalizedSectionBuilder } = useRefsStore();
const plan = ref<PlanRefQueryResult | null>(null);
const cost = ref<PersonalizedSectionBuilderRefQueryResult | null>(null);

if (props.selectPlan?.reference?._ref) {
  plan.value = (await getResolvedPlan(props.selectPlan?.reference?._ref)).value;
  cost.value = (
    await getResolvedPersonalizedSectionBuilder(plan.value?.price?.reference?._ref)
  ).value;
}

const contentRef = ref<HTMLElement | null>(null);
const contentHeight = ref('0px');
const rs = useResponsive().getDefault();
const toggleFeatures = () => {
  if (!rs.isLg.value) {
    showList.value = !showList.value;
  }
};
onMounted(() => {
  if (!rs.isLg.value && showList && contentRef.value) {
    showList.value = false;
    contentHeight.value = `${contentRef.value.scrollHeight}px`;
  } else {
    contentHeight.value = '0px';
  }
});
</script>
<style>
.slide-enter-to,
.slide-leave-from {
  max-height: v-bind(contentHeight); /* Adjust based on your content */
  overflow: hidden;
}

.plans-section .plan-item .text-one-column h4 {
  line-height: 1.5rem !important;
}
</style>
